'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Copy,
  Code2,
  Eye,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  IconLibraryEditorDialog,
  type IconEditorDraft,
} from '@/components/painel/icon-library/IconLibraryEditorDialog';
import { IconSvgPreview } from '@/components/painel/icon-library/IconSvgPreview';
import { invalidateIconLibraryCache } from '@/components/icons/Icon';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
  painelSelectContentClass,
  painelSurfaceClass,
} from '@/components/painel/painel-styles';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import {
  categoryLabel,
  ICON_LIBRARY_CATEGORIES,
  type IconLibraryItem,
} from '@/lib/icon-library';
import { sessionHasPermission } from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';
import {
  createAdminIcon,
  deleteAdminIcon,
  fetchAdminIcon,
  fetchAdminIcons,
  importPublicIcons,
  updateAdminIcon,
  uploadAdminIcon,
} from '@/services/icon-library.service';

async function copyText(text: string, okMsg: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    toast.error('Não foi possível copiar.');
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function PainelIconLibraryPage() {
  const { session } = usePainelSession();
  const canManage = sessionHasPermission(session, 'icons.manage');

  const [items, setItems] = useState<IconLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('name_asc');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<IconLibraryItem | null>(null);
  const [viewing, setViewing] = useState<IconLibraryItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await fetchAdminIcons({ q, category, sort });
      setItems(list);
    } catch {
      toast.error('Não foi possível carregar a biblioteca de ícones.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [canManage, q, category, sort]);

  useEffect(() => {
    const t = window.setTimeout(() => void load(), 200);
    return () => window.clearTimeout(t);
  }, [load]);

  const deleteTarget = useMemo(
    () => items.find((i) => i.id === deleteId) ?? null,
    [items, deleteId]
  );

  const openCreate = () => {
    setEditorMode('create');
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = async (item: IconLibraryItem) => {
    try {
      const full = await fetchAdminIcon(item.id);
      setEditing(full);
      setEditorMode('edit');
      setEditorOpen(true);
    } catch {
      toast.error('Não foi possível abrir o ícone.');
    }
  };

  const openView = async (item: IconLibraryItem) => {
    try {
      const full = await fetchAdminIcon(item.id);
      setViewing(full);
    } catch {
      toast.error('Não foi possível carregar o preview.');
    }
  };

  const handleSave = async (draft: IconEditorDraft) => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        slug: draft.slug.trim() || undefined,
        category: draft.category,
        svgContent: draft.svgContent,
        description: draft.description.trim() || null,
        tags: draft.tags,
        renderMode: draft.renderMode,
      };
      const result =
        editorMode === 'edit' && editing
          ? await updateAdminIcon(editing.id, payload)
          : await createAdminIcon(payload);

      if (result.hadDangerous || result.warnings.length) {
        toast.warning('Ícone guardado com sanitização.', {
          description: result.warnings.join(' ') || 'Conteúdo perigoso removido.',
        });
      } else {
        toast.success(editorMode === 'edit' ? 'Ícone atualizado.' : 'Ícone criado.');
      }
      invalidateIconLibraryCache();
      setEditorOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao guardar ícone.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadInEditor = async (file: File, draft: IconEditorDraft) => {
    setSaving(true);
    try {
      const text = await file.text();
      if (editorMode === 'create') {
        const result = await uploadAdminIcon(file, {
          name: draft.name.trim() || undefined,
          slug: draft.slug.trim() || undefined,
          category: draft.category,
          description: draft.description.trim() || null,
          tags: draft.tags,
          renderMode: draft.renderMode,
        });
        if (result.hadDangerous) {
          toast.warning('Upload concluído com sanitização.', {
            description: result.warnings.join(' '),
          });
        } else {
          toast.success('Ícone carregado.');
        }
        setEditorOpen(false);
        invalidateIconLibraryCache();
        await load();
      } else if (editing) {
        const result = await updateAdminIcon(editing.id, {
          name: draft.name.trim(),
          slug: draft.slug.trim(),
          category: draft.category,
          description: draft.description.trim() || null,
          tags: draft.tags,
          renderMode: draft.renderMode,
          svgContent: text,
        });
        toast.success('SVG atualizado a partir do ficheiro.');
        if (result.hadDangerous) {
          toast.warning(result.warnings.join(' ') || 'Conteúdo sanitizado.');
        }
        setEditorOpen(false);
        invalidateIconLibraryCache();
        await load();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no upload.');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const n = await importPublicIcons();
      toast.success(n ? `${n} ícone(s) importado(s).` : 'Nada novo para importar.');
      invalidateIconLibraryCache();
      await load();
    } catch {
      toast.error('Falha ao importar public/icons.');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    try {
      await deleteAdminIcon(deleteId);
      toast.success('Ícone excluído.');
      setDeleteId(null);
      invalidateIconLibraryCache();
      await load();
    } catch {
      toast.error('Não foi possível excluir.');
    }
  };

  if (!canManage) {
    return (
      <div className={cn(painelSurfaceClass, 'p-8')}>
        <h1 className="font-poppins text-xl font-bold text-white">Icon Library</h1>
        <p className="mt-2 font-inter text-sm text-slate-400">
          Não tem permissão para gerir a biblioteca de ícones.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            Icon Library
          </h1>
          <p className="mt-1 max-w-2xl font-inter text-sm text-slate-400">
            Biblioteca centralizada de ícones SVG reutilizáveis no CMS. Os ficheiros em{' '}
            <code className="text-sky-300/80">/icons/</code> do site permanecem intactos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className={painelButtonOutlineClass}
            onClick={() => void handleImport()}
            disabled={importing}
          >
            {importing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Importar /icons
          </Button>
          <Button type="button" className={painelButtonPrimaryClass} onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Adicionar ícone
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou slug…"
            className={cn(painelInputClass, 'pl-9')}
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className={cn(painelInputClass, 'h-10 w-full sm:w-44')}>
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent className={painelSelectContentClass}>
            <SelectItem value="all">Todas</SelectItem>
            {ICON_LIBRARY_CATEGORIES.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className={cn(painelInputClass, 'h-10 w-full sm:w-48')}>
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent className={painelSelectContentClass}>
            <SelectItem value="name_asc">Nome A–Z</SelectItem>
            <SelectItem value="name_desc">Nome Z–A</SelectItem>
            <SelectItem value="newest">Atualizados</SelectItem>
            <SelectItem value="oldest">Mais antigos</SelectItem>
            <SelectItem value="category">Categoria</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="font-inter text-sm">A carregar…</span>
        </div>
      ) : items.length === 0 ? (
        <div className={cn(painelSurfaceClass, 'p-10 text-center')}>
          <p className="font-poppins text-base font-semibold text-white">Nenhum ícone</p>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Adicione um SVG ou importe os ícones existentes de public/icons.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => (
            <article
              key={item.id}
              className={cn(
                painelSurfaceClass,
                'flex flex-col gap-3 p-4 transition hover:border-sky-400/30'
              )}
            >
              <div className="flex items-start gap-3">
                <IconSvgPreview
                  url={item.url}
                  renderMode={item.renderMode}
                  size={48}
                  background="dark"
                />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate font-poppins text-sm font-semibold text-white">
                    {item.name}
                  </h2>
                  <p className="truncate font-mono text-[11px] text-sky-300/70">{item.slug}</p>
                  <p className="mt-1 font-inter text-[11px] text-slate-500">
                    {categoryLabel(item.category)}
                  </p>
                </div>
              </div>
              <div className="space-y-0.5 font-inter text-[10px] text-slate-500">
                <p>Criado: {formatDate(item.createdAt)}</p>
                <p>Atualizado: {formatDate(item.updatedAt)}</p>
              </div>
              <div className="mt-auto flex flex-wrap gap-1 border-t border-white/5 pt-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-300 hover:bg-white/10 hover:text-white"
                  onClick={() => void openEdit(item)}
                  title="Editar"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-300 hover:bg-white/10 hover:text-white"
                  onClick={() => void openView(item)}
                  title="Visualizar"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-300 hover:bg-white/10 hover:text-white"
                  onClick={() => void copyText(item.slug, 'Identificador copiado.')}
                  title="Copiar identificador"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-300 hover:bg-white/10 hover:text-white"
                  onClick={async () => {
                    const full = item.svgContent
                      ? item
                      : await fetchAdminIcon(item.id).catch(() => null);
                    if (!full?.svgContent) {
                      toast.error('SVG indisponível.');
                      return;
                    }
                    void copyText(full.svgContent, 'SVG copiado.');
                  }}
                  title="Copiar SVG"
                >
                  <Code2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-8 px-2 text-rose-300 hover:bg-rose-500/15 hover:text-rose-100"
                  onClick={() => setDeleteId(item.id)}
                  title="Excluir"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}

      <IconLibraryEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        mode={editorMode}
        initial={editing}
        saving={saving}
        onSave={handleSave}
        onUploadFile={handleUploadInEditor}
      />

      <PainelConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Excluir ícone?"
        description={
          deleteTarget
            ? `O ícone «${deleteTarget.name}» (${deleteTarget.slug}) será removido da biblioteca. Referências no CMS podem deixar de resolver.`
            : 'Este ícone será removido da biblioteca.'
        }
        confirmLabel="Excluir"
        onConfirm={() => void handleDelete()}
      />

      {viewing ? (
        <Dialog open onOpenChange={(o) => !o && setViewing(null)}>
          <DialogContent
            showCloseButton={false}
            className="max-w-md border-0 bg-[#030023] p-0 shadow-none"
          >
            <DialogTitle className="sr-only">{viewing.name}</DialogTitle>
            <PainelPermissionsDialogShell>
              <div className="border-b border-white/10 bg-blue-950/30 px-6 py-5">
                <h2 className="font-poppins text-lg font-bold text-white">{viewing.name}</h2>
                <p className="mt-1 font-mono text-xs text-sky-300/70">{viewing.slug}</p>
                <p className="mt-1 font-inter text-xs text-slate-500">
                  {categoryLabel(viewing.category)}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 px-6 py-8">
                <div className="space-y-2 text-center">
                  <IconSvgPreview
                    svgContent={viewing.svgContent}
                    url={viewing.url}
                    renderMode={viewing.renderMode}
                    size={96}
                    background="dark"
                  />
                  <p className="font-inter text-[10px] text-slate-500">Fundo escuro</p>
                </div>
                <div className="space-y-2 text-center">
                  <IconSvgPreview
                    svgContent={viewing.svgContent}
                    url={viewing.url}
                    renderMode={viewing.renderMode}
                    size={96}
                    background="light"
                  />
                  <p className="font-inter text-[10px] text-slate-500">Fundo claro</p>
                </div>
              </div>
              <div className="flex justify-end border-t border-white/10 px-6 py-4">
                <Button
                  type="button"
                  variant="ghost"
                  className={painelModalCancelButtonClass}
                  onClick={() => setViewing(null)}
                >
                  Fechar
                </Button>
              </div>
            </PainelPermissionsDialogShell>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
